<template>
  <!-- Mobile: Fullscreen Overlay -->
  <v-overlay
    v-if="isXs"
    :model-value="isOpen"
    class="search-overlay-mobile"
    persistent
    no-click-animation
  >
    <v-card
      class="search-card-mobile"
      flat
      tile
    >
      <!-- Mobile Header -->
      <v-card-title class="search-header">
        <v-row no-gutters align="center">
          <v-col cols="1">
            <v-btn
              icon
              variant="text"
              @click="closeSearch"
              size="small"
            >
              <v-icon color="grey-lighten-1">mdi-arrow-left</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="10">
            <v-text-field
              v-model="query"
              :placeholder="placeholder"
              variant="solo"
              flat
              hide-details
              bg-color="transparent"
              class="search-input-mobile"
              ref="searchInputRef"
              @keydown.esc="closeSearch"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey-lighten-1" size="20">mdi-magnify</v-icon>
              </template>
              <template v-slot:append-inner>
                <v-btn
                  v-if="query"
                  icon
                  variant="text"
                  size="x-small"
                  @click="clearQuery"
                >
                  <v-icon color="grey-lighten-1">mdi-close</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="1">
            <v-btn
              icon
              variant="text"
              @click="closeSearch"
              size="small"
            >
              <v-icon color="grey-lighten-1">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- Mobile Content -->
      <v-card-text class="search-content-mobile">
        <SearchContent
          v-model:active-tab="activeTab"
          :query="query"
          :results="results"
          :loading="loading"
          @result-click="handleResultClick"
          @team-click="handleTeamClick"
          @league-click="handleLeagueClick"
          @close-search="closeSearch"
        />
      </v-card-text>
    </v-card>
  </v-overlay>

  <!-- Desktop: Modal Dialog -->
  <v-dialog
    v-else
    v-model="isOpen"
    max-width="600"
    persistent
    no-click-animation
  >
    <v-card class="search-card-desktop">
      <!-- Desktop Header -->
      <v-card-title class="search-header">
        <v-row no-gutters align="center">
          <v-col cols="11">
            <v-text-field
              v-model="query"
              :placeholder="placeholder"
              variant="solo"
              flat
              hide-details
              bg-color="rgba(255,255,255,0.05)"
              class="search-input-desktop"
              ref="searchInputRef"
              @keydown.esc="closeSearch"
            >
              <template v-slot:prepend-inner>
                <v-icon color="grey-lighten-1" size="20">mdi-magnify</v-icon>
              </template>
              <template v-slot:append-inner>
                <v-btn
                  v-if="query"
                  icon
                  variant="text"
                  size="x-small"
                  @click="clearQuery"
                >
                  <v-icon color="grey-lighten-1">mdi-close</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="1" class="text-right">
            <v-btn
              icon
              variant="text"
              @click="closeSearch"
              size="small"
            >
              <v-icon color="grey-lighten-1">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- Desktop Content -->
      <v-card-text class="search-content-desktop">
        <SearchContent
          v-model:active-tab="activeTab"
          :query="query"
          :results="results"
          :loading="loading"
          @result-click="handleResultClick"
          @team-click="handleTeamClick"
          @league-click="handleLeagueClick"
          @close-search="closeSearch"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useDisplay } from 'vuetify'
import axios from '@/axios'
import SearchContent from './SearchContent.vue'

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: "Pesquisar..."
  },
  minChars: {
    type: Number,
    default: 1
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'result-click'])

// Composables
const { width } = useDisplay()

// Reactive state
const query = ref('')
const activeTab = ref('leagues')
const results = ref([])
const loading = ref(false)
const searchInputRef = ref(null)

// Computed
const isXs = computed(() => width.value < 700)
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Debounce timeout
let debounceTimeout = null

// Watchers
watch(query, (newQuery) => {
  clearTimeout(debounceTimeout)
  
  if (newQuery.length < props.minChars) {
    results.value = []
    return
  }
  
  debounceTimeout = setTimeout(fetchResults, 300)
})

watch(activeTab, () => {
  clearTimeout(debounceTimeout)
  
  if (query.value.length < props.minChars) {
    results.value = []
    return
  }
  
  fetchResults()
})

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      setTimeout(() => {
        searchInputRef.value?.focus()
      }, 100)
    })
  } else {
    // Opcional: limpar estado ao fechar
    // query.value = ''
    // results.value = []
  }
})

// Methods
async function fetchResults() {
  loading.value = true
  
  try {
    let data = []
    
    if (activeTab.value === 'users') {
      const response = await axios.get(`/users/search?username=${query.value}`)
      data = response.data
    } else if (activeTab.value === 'teams') {
      const response = await axios.get(`/teams/search?name=${query.value}`)
      data = response.data
    } else if (activeTab.value === 'leagues') {
      const response = await axios.get(`/leagues/search?name=${query.value}`)
      data = response.data
    }
    
    results.value = data
  } catch (error) {
    console.error("Erro na busca:", error)
    results.value = []
  } finally {
    loading.value = false
  }
}

function closeSearch() {
  isOpen.value = false
}

function clearQuery() {
  query.value = ''
  results.value = []
}

function handleResultClick(username) {
  closeSearch()
  emit('result-click', { type: 'user', username })
}

function handleTeamClick(teamName, teamId) {
  closeSearch()
  emit('result-click', { type: 'team', name: teamName, id: teamId })
}

function handleLeagueClick(leagueName, leagueId) {
  closeSearch()
  emit('result-click', { type: 'league', name: leagueName, id: leagueId })
}

// Keyboard shortcuts
function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeSearch()
  }
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    isOpen.value = true
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  clearTimeout(debounceTimeout)
})
</script>

<style scoped>
/* Mobile Styles */
.search-overlay-mobile {
  z-index: 9999;
}

.search-card-mobile {
  width: 100vw;
  height: 100vh;
  background-color: #14181c;
}

.search-header {
  padding: 8px 16px;
  background-color: #0e343e;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.search-input-mobile :deep(.v-field__input) {
  padding: 8px 0;
  min-height: 40px;
  font-size: 16px;
  color: var(--cream);
}

.search-content-mobile {
  padding: 0;
  height: calc(100vh - 80px);
  overflow-y: auto;
}

/* Desktop Styles */
.search-card-desktop {
  background-color: #14181c;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}

.search-input-desktop :deep(.v-field__input) {
  padding: 6px 0;
  min-height: 4px;
  font-size: 16px;
  color: var(--cream);
}

.search-content-desktop {
  padding: 0 !important;
  max-height: 60vh;
  overflow-y: auto;
}

/* Common Styles */
.search-input-mobile :deep(.v-field__outline),
.search-input-desktop :deep(.v-field__outline) {
  display: none;
}

.search-input-mobile :deep(.v-field),
.search-input-desktop :deep(.v-field) {
  box-shadow: none !important;
}
</style>